import { organizationFormConfig } from '@acme/domains';
import {
  FormControls,
  useFormModel,
  type FieldConfig,
  type FormConfig,
} from '@acme/foundation';
import { Card } from 'flowbite-react';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

export type OrganizationFormDefaultData = {
  name?: string | null;
  inn?: string | null;
  ogrn?: string | null;
  legalAddress?: string | null;
  actualAddress?: string | null;
  phoneNumber?: string | null;
  responsiblePersonName?: string | null;
  bankDetail?: {
    accountNumber?: string | null;
    name?: string | null;
    identificationCode?: string | null;
    correspondentAccount?: string | null;
  } | null;
  role?: unknown;
};

export interface OrganizationFormProps {
  defaultData?: OrganizationFormDefaultData | null;
  isEdit: boolean;
  formRef?: React.RefObject<{
    submit: () => Promise<OrganizationFormDefaultData | null>;
  }>;
  onDataChange?: (data: OrganizationFormDefaultData) => void;
  headerExtra?: ReactNode;
  headerActions?: ReactNode;
}

export const OrganizationGeneralForm = ({
  defaultData,
  isEdit,
  formRef,
  onDataChange,
  headerExtra,
  headerActions,
}: OrganizationFormProps) => {
  const formConfig = useMemo(() => {
    const config =
      organizationFormConfig() as unknown as FormConfig<OrganizationFormDefaultData>;
    config.controls.forEach(
      (control: FieldConfig<OrganizationFormDefaultData>) => {
        if (!control.rules) control.rules = [];
        control.rules.push({
          when: () => !isEdit,
          set: { disabled: true },
        });
      }
    );
    return config;
  }, [isEdit]);

  const form = useFormModel({
    config: formConfig,
    ctx: defaultData || {},
  });

  if (formRef) {
    (
      formRef as React.MutableRefObject<{
        submit: () => Promise<OrganizationFormDefaultData | null>;
      } | null>
    ).current = {
      submit: async () => {
        let formData: OrganizationFormDefaultData | null = null;
        await form.submit(async (finalModel: OrganizationFormDefaultData) => {
          formData = finalModel;
          onDataChange?.(finalModel);
        });
        return formData;
      },
    };
  }

  return (
    <Card>
      <div className="mb-6 flex items-start justify-between gap-6">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold">Организация</h2>
          {headerExtra}
        </div>
        {headerActions}
      </div>

      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-3 gap-6">
          <FormControls form={form} />
        </div>
      </div>
    </Card>
  );
};
